import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Select, Checkbox, Switch, message, Popconfirm, Typography } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { MembershipProductService } from '../../Services/MembershipProductService';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import { WebApiModelsMembershipProduct, WebApiMembershipProductDtoCreateMembershipProductDto, WebApiMembershipProductDtoUpdateMembershipProductDto, WebApiIntegrationsDtoExternalArticleDto } from '../../Repository/eventbookyapi';
import { TextService } from '../../Services/TextService';
import LoadingSpinner from '../../Components/Shared/LoadingSpinner';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftSettings from '../Account/LeftSettings';
import { BillingFrequency } from '../../Types/BillingFrequency';
import { MembershipProductTypeOptions } from '../../Helpers/MembershipProductType';
import { sortAlphabetically, sortNumber } from '../../Helpers/TableSortHelper';

const { Title } = Typography;
const { Option } = Select;

// ExternalArticleNumber is one shared field (see MembershipProduct.ExternalArticleNumber) used to
// resolve an article against whichever integration is invoicing - Fortnox and Spiris - not a
// Fortnox-only field, so the picker needs to offer both providers' synced articles.
interface TaggedExternalArticle extends WebApiIntegrationsDtoExternalArticleDto {
    provider: 'Fortnox' | 'Spiris';
}

const MembershipProducts: React.FC = () => {
    const [membershipProducts, setMembershipProducts] = useState<WebApiModelsMembershipProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState<WebApiModelsMembershipProduct | null>(null);
    const [externalArticles, setExternalArticles] = useState<TaggedExternalArticle[]>([]);
    const [hasFortnoxIntegration, setHasFortnoxIntegration] = useState(false);
    const [hasSpirisIntegration, setHasSpirisIntegration] = useState(false);
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    // Load membership products on component mount
    useEffect(() => {
        loadMembershipProducts();
        loadExternalArticles();
    }, []);

    const loadExternalArticles = async () => {
        try {
            const integrations = await IntegrationService.GetActiveIntegrations();
            const fortnoxConnected = integrations.some((i: any) => i.type === IntegrationType.Fortnox);
            const spirisConnected = integrations.some((i: any) => i.type === IntegrationType.Spiris);
            setHasFortnoxIntegration(fortnoxConnected);
            setHasSpirisIntegration(spirisConnected);

            const [fortnoxArticles, spirisArticles] = await Promise.all([
                fortnoxConnected ? IntegrationService.GetExternalArticles(IntegrationType.Fortnox) : Promise.resolve([]),
                spirisConnected ? IntegrationService.GetExternalArticles(IntegrationType.Spiris) : Promise.resolve([]),
            ]);

            setExternalArticles([
                ...fortnoxArticles.map(a => ({ ...a, provider: 'Fortnox' as const })),
                ...spirisArticles.map(a => ({ ...a, provider: 'Spiris' as const })),
            ]);
        } catch (error) {
            console.error('Error loading external articles:', error);
        }
    };

    const loadMembershipProducts = async () => {
        try {
            setLoading(true);
            const products = await MembershipProductService.GetAllMembershipProducts();
            setMembershipProducts(products);
        } catch (error) {
            console.error('Error loading membership products:', error);
            message.error(GetText('MembershipProducts_ErrorLoadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingProduct(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (product: WebApiModelsMembershipProduct) => {
        setEditingProduct(product);
        form.setFieldsValue({
            name: product.name,
            price: product.price,
            description: product.description,
            membershipProductType: product.membershipProductType,
            visiblePublic: product.visiblePublic || false,
            externalArticleNumber: product.externalArticleNumber
        });
        setModalVisible(true);
    };

    const handleDelete = async (productId: number) => {
        try {
            await MembershipProductService.DeleteMembershipProduct(productId);
            message.success(GetText('MembershipProducts_SuccessDeleted'));
            loadMembershipProducts();
        } catch (error) {
            console.error('Error deleting membership product:', error);
            message.error(GetText('MembershipProducts_ErrorDeleteFailed'));
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            if (editingProduct) {
                // Update existing product
                const updateData: WebApiMembershipProductDtoUpdateMembershipProductDto = {
                    name: values.name,
                    price: values.price,
                    description: values.description,
                    billingFrequency: BillingFrequency.Annually, // only option - field is hidden
                    membershipProductType: values.membershipProductType || 0,
                    visiblePublic: values.visiblePublic || false,
                    externalArticleNumber: values.externalArticleNumber || null
                };
                await MembershipProductService.UpdateMembershipProduct(editingProduct.id!, updateData);
                message.success(GetText('MembershipProducts_SuccessUpdated'));
            } else {
                // Create new product
                const createData: WebApiMembershipProductDtoCreateMembershipProductDto = {
                    name: values.name,
                    price: values.price,
                    description: values.description,
                    billingFrequency: BillingFrequency.Annually, // only option - field is hidden
                    membershipProductType: values.membershipProductType || 0,
                    visiblePublic: values.visiblePublic || false,
                    externalArticleNumber: values.externalArticleNumber || null
                };
                await MembershipProductService.CreateMembershipProduct(createData);
                message.success(GetText('MembershipProducts_SuccessCreated'));
            }

            setModalVisible(false);
            form.resetFields();
            loadMembershipProducts();
        } catch (error) {
            console.error('Error saving membership product:', error);
            message.error(GetText('MembershipProducts_ErrorSaveFailed'));
        }
    };

    const columns = [
        {
            title: GetText('MembershipProducts_ColumnId'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: GetText('MembershipProducts_ColumnName'),
            dataIndex: 'name',
            key: 'name',
            width: 200,
            sorter: (a: WebApiModelsMembershipProduct, b: WebApiModelsMembershipProduct) =>
                sortAlphabetically(a, b, 'name'),
        },
        {
            title: GetText('MembershipProducts_ColumnPrice'),
            dataIndex: 'price',
            key: 'price',
            width: 120,
            render: (price: number) => price ? `${price.toFixed(2)} SEK` : '-',
            sorter: (a: WebApiModelsMembershipProduct, b: WebApiModelsMembershipProduct) =>
                sortNumber(a.price, b.price),
        },
        {
            title: GetText('MembershipProducts_ColumnDescription'),
            dataIndex: 'description',
            key: 'description',
            width: 250,
            ellipsis: true,
        },
        {
            title: GetText('MembershipProducts_ColumnType'),
            dataIndex: 'membershipProductType',
            key: 'membershipProductType',
            width: 120,
            render: (type: number) => {
                const option = MembershipProductTypeOptions.find(opt => opt.value === type);
                return option ? GetText(option.textKey) : `Type ${type}`;
            },
            filters: MembershipProductTypeOptions.map(option => ({
                text: GetText(option.textKey),
                value: option.value
            })),
            onFilter: (value: boolean | React.Key, record: WebApiModelsMembershipProduct) =>
                record.membershipProductType === value,
        },
        {
            title: GetText('MembershipProducts_ColumnVisiblePublic'),
            dataIndex: 'visiblePublic',
            key: 'visiblePublic',
            width: 180,
            render: (visiblePublic: boolean) => (
                visiblePublic ? <span style={{ color: 'green' }}>✓</span> : <span style={{ color: 'red' }}>✗</span>
            ),
        },
        {
            title: GetText('MembershipProducts_ColumnActions'),
            key: 'actions',
            width: 150,
            render: (_: any, record: WebApiModelsMembershipProduct) => (
                <Space size="middle">
                    <EditOutlined
                        style={{ fontSize: "20px", cursor: "pointer" }}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title={GetText('MembershipProducts_DeleteTitle')}
                        description={GetText('MembershipProducts_DeleteDescription')}
                        onConfirm={() => handleDelete(record.id!)}
                        okText={GetText('MembershipProducts_ButtonYes')}
                        cancelText={GetText('MembershipProducts_ButtonNo')}
                    >
                        <DeleteOutlined
                            style={{ color: "red", fontSize: "20px", cursor: "pointer" }}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <LayoutLeftMenu title={GetText('MembershipProducts_Title')} menu={<LeftSettings selected={"membershipProducts"} />}>
                <LoadingSpinner loadingText={GetText('MembershipProducts_LoadingText')} />
            </LayoutLeftMenu>
        );
    }

    return (
        <LayoutLeftMenu title={GetText('MembershipProducts_Title')} menu={<LeftSettings selected={"membershipProducts"} />}>
            <div style={{ marginBottom: '24px' }}>
                <Typography.Paragraph>
                    {GetText('MembershipProducts_Description')}
                </Typography.Paragraph>
            </div>

            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    {GetText('MembershipProducts_ButtonAddNew')}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={membershipProducts}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />

            <Modal
                title={editingProduct ? GetText('MembershipProducts_ModalTitleEdit') : GetText('MembershipProducts_ModalTitleCreate')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                okText={editingProduct ? GetText('MembershipProducts_ButtonUpdate') : GetText('MembershipProducts_ButtonCreate')}
                cancelText={GetText('MembershipProducts_ButtonCancel')}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label={GetText('MembershipProducts_FieldProductType')}
                        name="membershipProductType"
                        rules={[{ required: true, message: GetText('MembershipProducts_ValidationProductTypeRequired') }]}
                    >
                        <Select
                            style={{ width: '100%' }}
                            placeholder={GetText('MembershipProducts_PlaceholderProductType')}
                        >
                            {MembershipProductTypeOptions.map(option => (
                                <Option key={option.value} value={option.value}>
                                    {GetText(option.textKey)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={GetText('MembershipProducts_FieldName')}
                        name="name"
                        rules={[
                            { required: true, message: GetText('MembershipProducts_ValidationNameRequired') },
                            { min: 2, message: GetText('MembershipProducts_ValidationNameMinLength') }
                        ]}
                    >
                        <Input placeholder={GetText('MembershipProducts_PlaceholderName')} />
                    </Form.Item>

                    <Form.Item
                        label={GetText('MembershipProducts_FieldPrice')}
                        name="price"
                        rules={[
                            { required: true, message: GetText('MembershipProducts_ValidationPriceRequired') },
                            { type: 'number', min: 0, message: GetText('MembershipProducts_ValidationPriceMinimum') }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder={GetText('MembershipProducts_PlaceholderPrice')}
                            min={0}
                            precision={2}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) as any}
                        />
                    </Form.Item>

                    <Form.Item
                        label={GetText('MembershipProducts_FieldDescription')}
                        name="description"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder={GetText('MembershipProducts_PlaceholderDescription')}
                        />
                    </Form.Item>

                    {/* Billing frequency hidden - BillingFrequencyOptions only ever has one value
                        (Annually), so there's nothing to choose. Submitted as a fixed value instead. */}

                    {/* Only shown at all once an integration exists to pick an article from - with
                        neither connected there's nothing meaningful to offer here. */}
                    {(hasFortnoxIntegration || hasSpirisIntegration) && (
                        <Form.Item
                            label={GetText('MembershipProducts_FieldExternalArticleNumber')}
                            name="externalArticleNumber"
                            tooltip={GetText('MembershipProducts_TooltipExternalArticleNumber')}
                        >
                            <Select
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                disabled={externalArticles.length === 0}
                                placeholder={
                                    externalArticles.length === 0
                                        ? GetText('MembershipProducts_PlaceholderNoSyncedArticles')
                                        : GetText('MembershipProducts_PlaceholderExternalArticleNumber')
                                }
                                optionFilterProp="label"
                            >
                                {externalArticles.map(article => (
                                    <Option key={`${article.provider}-${article.articleNumber}`} value={article.articleNumber!} label={`${article.articleNumber} ${article.name}`}>
                                        {article.articleNumber} — {article.name}
                                        {article.salesPrice != null ? ` — ${article.salesPrice.toFixed(2)} kr` : ''}
                                        {hasFortnoxIntegration && hasSpirisIntegration ? ` (${article.provider})` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="visiblePublic"
                        valuePropName="checked"
                    >
                        <Checkbox>
                            {GetText('MembershipProducts_FieldVisiblePublic')}
                        </Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
        </LayoutLeftMenu>
    );
};

export default MembershipProducts;